import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Badge } from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import CampaignStatsFilterCard from "../component/CampaignStatsFilterCard";
import { formatDateTime } from "../../../Components/Common/FormatDate";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const CampaignStats = () => {
  const [statsList, setStatsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    campaignId: "",
    startDate: "",
    endDate: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      campaignId: "",
      startDate: "",
      endDate: "",
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    fetchCampaignStats();
  }, [debouncedFilters]);

  async function fetchCampaignStats(currentFilters = debouncedFilters) {
    try {
      setLoading(true);

      // Format dates for API - handle both Date objects and strings
      let startDate = currentFilters.startDate;
      let endDate = currentFilters.endDate;

      if (startDate instanceof Date) {
        startDate = startDate.toISOString();
      }
      if (endDate instanceof Date) {
        endDate = endDate.toISOString();
      }

      const response = await CampaignService.getCampaignStats({
        campaignId: currentFilters.campaignId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      

      // Handle both array directly and object with data property
      const data = Array.isArray(response) ? response : response?.data || [];

      // Map data with unique keys for DataTable
      const mappedData = data.map((item, index) => ({
        id: item.campaignId || index,
        ...item,
      }));

      setStatsList(mappedData);
    } catch (error) {
      toast.error("Failed to fetch campaign statistics");
      console.error(error);
      setStatsList([]);
    } finally {
      setLoading(false);
    }
  }

  const columns = [
    {
      name: "SN",
      selector: (row, index) => index + 1,
      width: "60px",
      center: true,
    },
    {
      name: "Campaign Name",
      selector: (row) => row.campaignName,
      sortable: true,
      grow: 1.2,
    },
    {
      name: "Start Date",
      selector: (row) => formatDateTime(row.startDate),
      sortable: true,
    },
    {
      name: "End Date",
      selector: (row) =>
        row.endDate ? (
          formatDateTime(row.endDate)
        ) : (
          <Badge color="secondary">Ongoing</Badge>
        ),
      sortable: true,
    },
    {
      name: "Media Count",
      selector: (row) => row.totalAds || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Impressions",
      selector: (row) => row.totalImpressions || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Visible Seconds",
      selector: (row) => row.totalVisibleSeconds || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Played Seconds",
      selector: (row) => row.totalPlayedSeconds || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Completed Plays",
      selector: (row) => row.completedPlays || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Unique Devices",
      selector: (row) => row.uniqueDevices || 0,
      sortable: true,
      center: true,
    },
  ];

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Campaign Statistics" pageTitle="Campaigns" />
        </div>

        <Container fluid>
          <CampaignStatsFilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
          />

          <Card className="border-0 shadow-sm">
            <CardBody>
              <CustomDataTable
                responsive
                striped
                pagination
                // fixedHeader
                persistTableHead
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={(page) => setCurrentPage(page)}
                onChangeRowsPerPage={(newSize, page) => {
                  setPageSize(newSize);
                  setCurrentPage(1);
                }}
                progressPending={loading}
                customStyles={customStyles}
                progressComponent={
                  <div className="my-3 text-center">
                    <Triangle
                      visible={true}
                      height="80"
                      width="80"
                      color="#435ebe"
                      ariaLabel="triangle-loading"
                    />
                    <h5 className="mt-2">Loading...</h5>
                  </div>
                }
                columns={columns}
                data={statsList}
                noDataComponent={
                  <div className="py-4 text-center">
                    <p className="text-muted">No campaigns stats found</p>
                  </div>
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default CampaignStats;
